import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  RiSecurePaymentLine,
  RiVisaLine,
  RiMastercardLine,
  RiPaypalLine,
  RiArrowLeftLine,
  RiCheckLine,
  RiBankLine,
  RiSmartphoneLine,
  RiGiftLine,
  RiCoupon3Line,
} from "react-icons/ri";

const PaymentGateway = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const invoiceData = state?.invoiceData || {};
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [showCouponInput, setShowCouponInput] = useState(false);
  const [cardDetails, setCardDetails] = useState({
    number: "",
    name: "",
    expiry: "",
    cvv: "",
  });
  const [upiId, setUpiId] = useState("");
  const [selectedBank, setSelectedBank] = useState("");

  const invoiceAmount =
    state?.invoiceData?.items?.reduce(
      (sum, item) => sum + item.quantity * item.price * (1 + item.tax / 100),
      0,
    ) || 0;
  const [discountedAmount, setDiscountedAmount] = useState(invoiceAmount);

  useEffect(() => {
    if (!state?.invoiceData) {
      navigate("/billing");
      return;
    }
  }, [state, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate payment processing
    setTimeout(() => {
      setLoading(false);
      navigate("/billing/payment-success", {
        state: {
          paymentDetails: {
            transactionId: "TXN" + Date.now(),
            amount: discountedAmount,
            date: new Date().toISOString(),
            paymentMethod:
              paymentMethod === "card"
                ? "Credit Card"
                : paymentMethod === "upi"
                  ? "UPI"
                  : paymentMethod === "netbanking"
                    ? "Net Banking"
                    : "PayPal",
            invoiceNumber: invoiceData.invoiceNumber,
          },
        },
      });
    }, 2000);
  };

  const handleCouponApply = () => {
    // Mock coupon validation
    if (couponCode.toUpperCase() === "SAVE20") {
      setDiscountedAmount(invoiceAmount * 0.8); // 20% discount
      alert("Coupon applied successfully!");
    } else {
      alert("Invalid coupon code");
    }
  };

  const paymentMethods = [
    {
      id: "card",
      name: "Credit/Debit Card",
      icon: <RiVisaLine className="w-6 h-6" />,
      description: "Pay with Visa, Mastercard, or other cards",
    },
    {
      id: "upi",
      name: "UPI Payment",
      icon: <RiSmartphoneLine className="w-6 h-6" />,
      description: "Pay using any UPI app",
    },
    {
      id: "netbanking",
      name: "Net Banking",
      icon: <RiBankLine className="w-6 h-6" />,
      description: "Pay directly from your bank account",
    },
    {
      id: "paypal",
      name: "PayPal",
      icon: <RiPaypalLine className="w-6 h-6" />,
      description: "Pay using your PayPal account",
    },
  ];

  const popularBanks = [
    { id: "sbi", name: "State Bank of India" },
    { id: "hdfc", name: "HDFC Bank" },
    { id: "icici", name: "ICICI Bank" },
    { id: "axis", name: "Axis Bank" },
    { id: "other", name: "Other Banks" },
  ];

  const renderPaymentForm = () => {
    switch (paymentMethod) {
      case "card":
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="label">Card Number</label>
              <div className="relative">
                <input
                  type="text"
                  className="input pl-10"
                  placeholder="1234 5678 9012 3456"
                  maxLength="19"
                  value={cardDetails.number}
                  onChange={(e) =>
                    setCardDetails({
                      ...cardDetails,
                      number: e.target.value
                        .replace(/\s/g, "")
                        .replace(/(\d{4})/g, "$1 ")
                        .trim(),
                    })
                  }
                  required
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex gap-2">
                  <RiVisaLine className="w-6 h-6 text-blue-600" />
                  <RiMastercardLine className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Cardholder Name</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Name on card"
                  value={cardDetails.name}
                  onChange={(e) =>
                    setCardDetails({ ...cardDetails, name: e.target.value })
                  }
                  required
                />
              </div>
              <div>
                <label className="label">CVV</label>
                <input
                  type="text"
                  className="input"
                  placeholder="123"
                  maxLength="3"
                  value={cardDetails.cvv}
                  onChange={(e) =>
                    setCardDetails({
                      ...cardDetails,
                      cvv: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  required
                />
              </div>
            </div>

            <div>
              <label className="label">Expiry Date</label>
              <input
                type="text"
                className="input"
                placeholder="MM/YY"
                maxLength="5"
                value={cardDetails.expiry}
                onChange={(e) =>
                  setCardDetails({
                    ...cardDetails,
                    expiry: e.target.value
                      .replace(/\D/g, "")
                      .replace(/(\d{2})(\d)/, "$1/$2"),
                  })
                }
                required
              />
            </div>
          </form>
        );

      case "upi":
        return (
          <div className="space-y-4">
            <div>
              <label className="label">UPI ID</label>
              <div className="relative">
                <input
                  type="text"
                  className="input"
                  placeholder="username@upi"
                  value={upiId}
                  onChange={(e) => setUpiId(e.target.value)}
                  required
                />
              </div>
              <p className="text-sm text-gray-500 mt-2">
                Enter your UPI ID (e.g., mobilenumber@upi)
              </p>
            </div>
            <div className="flex gap-4">
              <img src="/gpay.png" alt="Google Pay" className="h-8" />
              <img src="/phonepe.png" alt="PhonePe" className="h-8" />
              <img src="/paytm.png" alt="Paytm" className="h-8" />
            </div>
          </div>
        );

      case "netbanking":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {popularBanks.map((bank) => (
                <button
                  key={bank.id}
                  type="button"
                  className={`p-4 border rounded-lg text-left transition-all ${
                    selectedBank === bank.id
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-primary-200"
                  }`}
                  onClick={() => setSelectedBank(bank.id)}
                >
                  <div className="flex items-center gap-3">
                    <RiBankLine
                      className={`w-5 h-5 ${
                        selectedBank === bank.id
                          ? "text-primary-600"
                          : "text-gray-400"
                      }`}
                    />
                    <span>{bank.name}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        );

      case "paypal":
        return (
          <div className="text-center p-8">
            <button className="btn btn-primary w-full">
              Continue with PayPal
            </button>
            <p className="text-sm text-gray-500 mt-4">
              You will be redirected to PayPal to complete your payment
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-full mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
        >
          <RiArrowLeftLine className="w-5 h-5" />
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Complete Payment</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <div className="flex items-center gap-3 mb-6">
              <RiSecurePaymentLine className="w-6 h-6 text-primary-600" />
              <div>
                <h2 className="text-xl font-semibold">Payment Method</h2>
                <p className="text-sm text-gray-500">
                  Choose your preferred payment method
                </p>
              </div>
            </div>

            {/* Payment Methods */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {paymentMethods.map((method) => (
                <button
                  key={method.id}
                  className={`flex items-start gap-4 p-4 border rounded-lg transition-all ${
                    paymentMethod === method.id
                      ? "border-primary-500 bg-primary-50"
                      : "border-gray-200 hover:border-primary-200"
                  }`}
                  onClick={() => setPaymentMethod(method.id)}
                >
                  <div
                    className={`p-2 rounded-lg ${
                      paymentMethod === method.id
                        ? "bg-primary-100 text-primary-600"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    {method.icon}
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{method.name}</span>
                      {paymentMethod === method.id && (
                        <RiCheckLine className="text-primary-600" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500">
                      {method.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>

            {/* Payment Form */}
            {renderPaymentForm()}
          </div>
        </div>

        <div className="lg:col-span-1 space-y-6">
          {/* Order Summary */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Order Summary</h2>
            <div className="space-y-3">
              <div className="flex justify-between text-gray-600">
                <span>Subtotal</span>
                <span>${invoiceAmount.toFixed(2)}</span>
              </div>
              {discountedAmount !== invoiceAmount && (
                <div className="flex justify-between text-green-600">
                  <span>Discount</span>
                  <span>-${(invoiceAmount - discountedAmount).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-semibold border-t pt-2">
                <span>Total</span>
                <span>${discountedAmount.toFixed(2)}</span>
              </div>
            </div>

            {/* Coupon Section */}
            <div className="mt-4">
              {showCouponInput ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      className="input flex-1"
                      placeholder="Enter coupon code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={handleCouponApply}
                    >
                      Apply
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
                  onClick={() => setShowCouponInput(true)}
                >
                  <RiCoupon3Line />
                  <span>Add Coupon Code</span>
                </button>
              )}
            </div>

            {/* Pay Button */}
            <button
              onClick={handleSubmit}
              className="btn btn-primary w-full mt-6"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin">⌛</span> Processing...
                </span>
              ) : (
                `Pay $${discountedAmount.toFixed(2)}`
              )}
            </button>
          </div>

          {/* Security Note */}
          <div className="text-center text-sm text-gray-500">
            <p className="flex items-center justify-center gap-2">
              <RiSecurePaymentLine />
              Your payment is secured by industry-standard encryption
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentGateway;
